import { type ReactNode } from 'react';
import { Layers, Moon, Sun } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';

/** Shared chrome for every auth screen: theme-aware backdrop, theme toggle,
 *  brand mark, and a centered card. Form vs. status content goes in `children`. */
export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
}) {
  const { resolvedTheme, setTheme } = useTheme();

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      {/* Decorative, theme-aware backdrop */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -top-32 left-1/2 h-80 w-[40rem] -translate-x-1/2 rounded-full bg-foreground/[0.06] blur-3xl" />
        <div className="absolute -bottom-24 right-[-6rem] h-72 w-72 rounded-full bg-foreground/[0.05] blur-3xl" />
        <div className="absolute -bottom-24 left-[-6rem] h-72 w-72 rounded-full bg-foreground/[0.04] blur-3xl" />
      </div>

      {/* Theme toggle — persists across the app (same store as the dashboard) */}
      <button
        type="button"
        onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
        className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card/60 text-muted-foreground backdrop-blur transition-colors hover:bg-muted hover:text-foreground"
        aria-label={resolvedTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <div className="relative z-10 w-full max-w-sm rounded-2xl border border-border bg-card/80 p-8 shadow-xl backdrop-blur-sm">
        {/* Brand */}
        <div className="mb-7 flex flex-col items-center text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-border bg-background shadow-sm">
            <Layers className="h-6 w-6 text-foreground" />
          </div>
          <span className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Ghost Control
          </span>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </div>
        {children}
      </div>
    </div>
  );
}
